import React, { useCallback, useMemo } from 'react';
import { Alert, Box, Paper } from '@mui/material';
import { PullRequestDetails } from '@finvari/deliver';
import { useConfig } from '../../contexts/ConfigContext';
import { useWorkflow, computeNodeCounts } from '../../workflow/useWorkflow';
import { NODE } from '../../workflow/workflowConfig';
import { usePrPageUrlState, AUTHOR_ALL } from './usePrPageUrlState';
import WorkflowDiagram from '../../components/workflow/WorkflowDiagram';
import { DIAGRAM_HEIGHT } from '../../components/workflow/workflowLayout';
import AuthorFilter from './AuthorFilter';
import PullRequestList from './PullRequestList';
import PullRequestPanel from './PullRequestPanel';

// How far the diagram drops when the window is too narrow for it to clear the author dropdown.
const DIAGRAM_NARROW_OFFSET = 50;

const PullRequests: React.FC = () => {
  const { userProfile } = useConfig();
  const { workflowByPr, authors, prCountByAuthor, openPrs, mergedPrs, loading, error } = useWorkflow();
  const { stage, author, prNumber, setStage, setAuthor, setPr } = usePrPageUrlState();

  // The author filter narrows both the list and the diagram counts, so share one predicate.
  const filterByAuthor = useCallback(
    (list: PullRequestDetails[]) =>
      author === AUTHOR_ALL ? list : list.filter(pr => pr.pr.user?.login === author),
    [author]
  );

  // Diagram counts reflect the author filter (across open + recently-merged PRs), independent of the
  // selected node — clicking a node filters the list, not the counts.
  const { nodeCounts, priorityCounts } = useMemo(
    () => computeNodeCounts(filterByAuthor([...openPrs, ...mergedPrs]), workflowByPr),
    [filterByAuthor, openPrs, mergedPrs, workflowByPr]
  );

  // Which PRs the list shows: the Merged node draws from the recently-merged window; every other
  // node (and no selection) draws from open PRs. Then narrow by the selected node and author.
  const listPrs = useMemo(() => {
    const base: PullRequestDetails[] = stage === NODE.merged ? mergedPrs : openPrs;
    let result = base;
    if (stage) {
      result = result.filter(pr => workflowByPr.get(pr.pr.number)?.nodeIds.includes(stage));
    }
    return filterByAuthor(result);
  }, [stage, filterByAuthor, openPrs, mergedPrs, workflowByPr]);

  // The selected PR may live outside the current list (state changed, or a deep link to a merged
  // PR), so look it up across everything we have loaded.
  const selectedPr = useMemo(() => {
    if (prNumber === null) return undefined;
    return [...openPrs, ...mergedPrs].find(pr => pr.pr.number === prNumber);
  }, [prNumber, openPrs, mergedPrs]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        p: 2,
        gap: 2,
        boxSizing: 'border-box',
      }}
    >
      {error && <Alert severity="error">{error}</Alert>}

      {/* Top: read-only workflow diagram sitting directly on the page background (no card), with the
          author filter overlaid at the top-right so the diagram gets the full width. */}
      <Box
        sx={theme => ({
          position: 'relative',
          height: DIAGRAM_HEIGHT,
          flexShrink: 0,
          // Below 1200px the top row's right-hand nodes reach far enough right to run under the
          // overlaid author dropdown, so drop the diagram clear of it. The dropdown is positioned
          // against the padding box, so it stays put while the diagram moves down.
          [theme.breakpoints.down('lg')]: {
            height: DIAGRAM_HEIGHT + DIAGRAM_NARROW_OFFSET,
            pt: DIAGRAM_NARROW_OFFSET + 'px',
          },
        })}
      >
        <WorkflowDiagram
          nodeCounts={nodeCounts}
          priorityCounts={priorityCounts}
          selectedStage={stage}
          onSelectStage={setStage}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 5,
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <AuthorFilter
            authors={authors}
            prCountByAuthor={prCountByAuthor}
            value={author}
            myLogin={userProfile?.githubUsername}
            onChange={setAuthor}
          />
        </Box>
      </Box>

      {/* Bottom: master-detail list + panel */}
      <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, minHeight: 0 }}>
        <Box sx={{ flexBasis: '40%', minWidth: 0, minHeight: 0, height: '100%' }}>
          <PullRequestList
            prs={listPrs}
            workflowByPr={workflowByPr}
            selectedPr={prNumber}
            onSelectPr={setPr}
            loading={loading}
            stage={stage}
            author={author}
          />
        </Box>
        <Paper variant="outlined" sx={{ flexBasis: '60%', minWidth: 0, minHeight: 0, overflow: 'hidden', borderRadius: 2 }}>
          <PullRequestPanel
            prNumber={prNumber}
            pr={selectedPr}
            openPrs={openPrs}
            loading={loading}
            onSelectPr={setPr}
            stage={stage}
            onSelectStage={setStage}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default PullRequests;
